import React, { useState } from 'react';
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio as RadioMui,
  Select,
  Switch as SwitchMui,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker as DatePickerMui, DateView } from '@mui/x-date-pickers';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import styles from './Form.module.scss';
import dayjs from 'dayjs';
import { DateTimePickerProps, DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

export const HiddenFile: React.FC<{
  onChange: (f: File | null) => void;
  reference: React.RefObject<HTMLInputElement>;
  accept?: string;
}> = ({ onChange, reference, accept }) => {
  return (
    <input
      value=""
      hidden={true}
      type="file"
      ref={reference}
      accept={accept ?? 'image/jpeg'}
      onChange={(event) => {
        const file = event.target.files ? event.target.files[0] : null;
        onChange(file);
      }}
    />
  );
};

export const Input: React.FC<{
  label: string;
  value: string;
  onChange: (s: string) => void;
  helperText?: string;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  isError?: boolean;
  placeholder?: string;
  InputLabelProps?: TextFieldProps['InputLabelProps'];
}> = (props) => {
  const {
    label,
    value,
    multiline,
    rows,
    onChange,
    helperText,
    required,
    disabled,
    maxLength,
    isError,
    placeholder,
    ...rest
  } = props;

  return (
    <TextField
      {...rest}
      required={required}
      fullWidth={true}
      error={isError}
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      helperText={helperText}
      inputProps={{ maxLength }}
      placeholder={placeholder}
    />
  );
};

export const Textarea: React.FC<{
  rows: number;
  value: string;
  onChange?: (v: string) => void;
  disabled?: boolean;
}> = ({ rows, value, onChange, disabled }) => {
  return (
    <div className={styles.textareaContainer}>
      <textarea
        className={styles.textarea}
        rows={rows}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
      />
    </div>
  );
};

export const Dropdown: React.FC<{
  required?: boolean;
  disabled?: boolean;
  label: string;
  value: string;
  onSelect: (s: string) => void;
  items: { label: string; value: string; disabled?: boolean; display?: JSX.Element }[];
  isError?: boolean;
}> = ({ required, disabled, label, value, onSelect, items, isError }) => {
  return (
    <FormControl
      required={required}
      fullWidth={true}
      error={isError}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        disabled={disabled}
        value={value}
        onChange={(e) => {
          onSelect(e.target.value);
        }}
      >
        {items.map((item) => {
          if (item.display === undefined || !item.display) {
            item.display = <div>{item.label}</div>;
          }

          return (
            <MenuItem
              value={item.value}
              key={item.value}
              disabled={item.disabled}
            >
              {item.display}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export const AutoCompleteDropdown: React.FC<{
  label: string;
  helperText?: string;
  data: { label: string; value: string }[];
  required?: boolean;
  disabled?: boolean;
  inputValue: string;
  onInputChange: (s: string) => void;
  onChange: (s: string) => void; // This is an issue with react-window somehow they did not trigger original onChange from MUI Autocomplete
}> = ({ label, helperText, data, disabled, required, inputValue, onInputChange, onChange }) => {
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const renderRow = (props_: ListChildComponentProps) => {
    const { data: items, index, style } = props_;
    const item = items[index];
    const itemLabel: string = item.props.children;

    return (
      <button
        className={styles.autoCompleteDropdownItem}
        onMouseDown={() => {
          setFirstLoad(true);
          onChange(itemLabel);
        }}
        style={style}
      >
        {itemLabel}
      </button>
    );
  };

  const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props_, ref) {
      const { children } = props_;
      const itemCount = Array.isArray(children) ? children.length : 0;

      return (
        <div
          className={styles.autoCompleteDropdownItems}
          ref={ref}
        >
          <FixedSizeList
            width={'100%'}
            height={320}
            itemSize={32}
            itemCount={itemCount}
            itemData={children}
          >
            {renderRow}
          </FixedSizeList>
        </div>
      );
    },
  );
  return (
    <Autocomplete
      inputValue={inputValue}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      options={data}
      filterOptions={(options) =>
        firstLoad === true
          ? options
          : options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
      }
      ListboxComponent={ListboxComponent}
      renderInput={(params) => (
        <TextField
          {...params}
          onChange={(e) => {
            setFirstLoad(false);
            onInputChange(e.target.value);
          }}
          label={label + (required ? ' *' : '')}
          helperText={helperText}
        />
      )}
    />
  );
};

export const DatePicker: React.FC<{
  required?: boolean;
  disabled?: boolean;
  min?: dayjs.Dayjs;
  max?: dayjs.Dayjs;
  defaultValue?: dayjs.Dayjs;
  value?: dayjs.Dayjs;
  label: string;
  onChange: (s: dayjs.Dayjs | null) => void;
  views?: DateView[];
  format?: string;
}> = ({ required, disabled, min, max, label, defaultValue, value, onChange, views, format }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl
        required={required}
        fullWidth
      >
        <DatePickerMui
          disabled={disabled}
          label={label + (required ? ' *' : '')}
          views={views || ['day']}
          minDate={min}
          maxDate={max}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          format={format || 'DD/MM/YYYY'}
        />
      </FormControl>
    </LocalizationProvider>
  );
};

export const DateTimePicker: React.FC<
  | {
      disabled?: boolean;
      value?: dayjs.Dayjs | null;
      label: string;
      format?: string;
      ampm?: boolean;
      onChange: (s: dayjs.Dayjs | null) => void;
    }
  | DateTimePickerProps<dayjs.Dayjs>
> = ({ disabled, label, value, format, ampm, onChange, ...props }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDateTimePicker
        label={label}
        disabled={disabled}
        value={value}
        onChange={onChange}
        format={format || 'DD/MM/YYYY HH:mm'}
        ampm={ampm}
        slotProps={{
          popper: {
            sx: {
              '& .MuiDateCalendar-root, .MuiMultiSectionDigitalClock-root': {
                maxHeight: 290,
              },
            },
          },
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

export const Switch: React.FC<{
  required?: boolean;
  disabled?: boolean;
  label: string;
  value: boolean;
  onChange: (s: boolean) => void;
}> = ({ required, disabled, label, value, onChange }) => {
  return (
    <FormGroup>
      <FormControlLabel
        required={required}
        disabled={disabled}
        control={
          <SwitchMui
            defaultChecked={value}
            onChange={() => {
              onChange(!value);
            }}
          />
        }
        label={label}
      />
    </FormGroup>
  );
};

export const Radio: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
  required?: boolean;
  disabled?: boolean;
}> = ({ label, checked, onChange, required, disabled }) => {
  return (
    <FormControlLabel
      required={required}
      disabled={disabled}
      control={
        <RadioMui
          checked={checked}
          onChange={onChange}
        />
      }
      label={label}
    />
  );
};
